// =============================================================================
// Data access layer — typed Supabase queries used across the app.
// =============================================================================
import { supabase, clerkAuthClient } from './supabase/client'
import type {
  Product, Customer, Invoice, InvoiceItem, Payment,
  Estimate, EstimateItem, Bill, BillItem, LaborHour, Worker, Appointment, TaxRate, Department,
  Vendor, GiftCertificate, Payout, Refund, RefundItem,
  Receiving, ReceivingDetailItem, InventoryPicture, Register, RegisterSession, RolodexContact,
  ScrappedInventory, QB_Account, SitePlan, SitePlanItem, CustomerMedia, CustomerMediaKind,
} from '../types'
import type { ListOptions, Sort } from '../lib/list'
import { customerLabel } from '../lib/format'

export type { ListOptions } from '../lib/list'

// -----------------------------------------------------------------------------
// Generic server-side list query. Search (OR of ilike + numeric eq), arbitrary
// column filters, full-dataset ordering, and ranged pagination — all executed
// in Postgres so sorting/filtering covers every row, not just the current page.
// -----------------------------------------------------------------------------
interface ListConfig {
  searchCols?: string[]      // ilike-matched text columns
  numericCols?: string[]     // eq-matched columns when the term is all digits
  defaultSort: Sort
  // Columns to select (default '*'). Use a narrow list + embedded relations for
  // big tables so the wire payload stays small.
  selectCols?: string
  // Row-count strategy. 'exact' is accurate but does a full count(*) on every
  // query — punishing on large tables. 'estimated' uses the planner estimate for
  // large sets (and exact for small ones), which keeps big lists snappy.
  countMode?: 'exact' | 'planned' | 'estimated'
}

const IMPOSSIBLE_ID = '00000000-0000-0000-0000-000000000000'

async function queryList<T>(table: string, opts: ListOptions, cfg: ListConfig): Promise<{ rows: T[]; count: number }> {
  const page = opts.page ?? 0
  const pageSize = opts.pageSize ?? 25
  let q = supabase.from(table).select(cfg.selectCols ?? '*', { count: cfg.countMode ?? 'exact' })

  const term = (opts.search ?? '').trim()
  if (term) {
    const ors: string[] = []
    for (const c of cfg.searchCols ?? []) ors.push(`${c}.ilike.%${term}%`)
    if (/^\d+$/.test(term)) for (const c of cfg.numericCols ?? []) ors.push(`${c}.eq.${term}`)
    q = ors.length ? q.or(ors.join(',')) : q.eq('id', IMPOSSIBLE_ID)
  }

  for (const f of opts.filters ?? []) {
    if (f.value === '' || f.value == null) continue
    if (f.op === 'eq') q = q.eq(f.column, f.value)
    else if (f.op === 'ilike') q = q.ilike(f.column, `%${f.value}%`)
    else if (f.op === 'gte') q = q.gte(f.column, f.value)
    else if (f.op === 'lte') q = q.lte(f.column, f.value)
  }

  const sort = opts.sort ?? cfg.defaultSort
  q = q.order(sort.column, { ascending: sort.dir === 'asc', nullsFirst: false })

  const { data, error, count } = await q.range(page * pageSize, (page + 1) * pageSize - 1)
  if (error) throw error
  return { rows: (data ?? []) as T[], count: count ?? 0 }
}

// ---- Products ----------------------------------------------------------------

export async function searchProducts(term: string, limit = 50): Promise<Product[]> {
  let q = supabase
    .from('products')
    .select('*')
    .eq('is_active', true)
    .order('name')
    .limit(limit)

  if (term.trim()) {
    const t = term.trim()
    // Match name, sku, scan number; for numeric terms also match the legacy
    // POS/IMS Inventory_Code so staff can search by item code.
    const ors = [`name.ilike.%${t}%`, `sku.ilike.%${t}%`, `scan_number.ilike.%${t}%`]
    if (/^\d+$/.test(t)) ors.push(`legacy_pos_inventory_code.eq.${t}`, `legacy_ims_inventory_code.eq.${t}`)
    q = supabase
      .from('products')
      .select('*')
      .or(ors.join(','))
      .order('name')
      .limit(limit)
  }

  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Product[]
}

export async function getProductByScan(scan: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .or(`scan_number.eq.${scan},sku.eq.${scan}`)
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as Product) ?? null
}

export async function getProduct(id: string): Promise<Product | null> {
  const { data, error } = await supabase.from('products').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return (data as Product) ?? null
}

export async function createProduct(p: Partial<Product>): Promise<Product> {
  const { data, error } = await supabase.from('products').insert(p).select().single()
  if (error) throw error
  return data as Product
}

export async function updateProduct(id: string, p: Partial<Product>): Promise<Product> {
  const { data, error } = await supabase.from('products').update(p).eq('id', id).select().single()
  if (error) throw error
  return data as Product
}

// Active products, optionally filtered by inventory type (legacy
// Materials_Price_Book inventory-type filter). Excludes depleted items.
export async function listProductsByType(type?: string, limit = 1000): Promise<Product[]> {
  let q = supabase.from('products').select('*').eq('is_active', true).order('name').limit(limit)
  if (type) q = q.eq('inventory_type', type)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Product[]
}

// Items flagged for the printed Materials Price Book (legacy InPriceBook).
export async function listPriceBookItems(): Promise<Product[]> {
  const { data, error } = await supabase.from('products').select('*')
    .eq('in_price_book', true).eq('is_active', true).order('inventory_type').order('name')
  if (error) throw error
  return (data ?? []) as Product[]
}

// Depleted products (legacy Depleted_Inventory list). Optional text filter on
// name / code; newest first.
export async function listDepletedProducts(term = '', limit = 500): Promise<Product[]> {
  let q = supabase.from('products').select('*').eq('is_depleted', true).order('name').limit(limit)
  const t = term.trim()
  if (t) {
    const ors = [`name.ilike.%${t}%`, `genus.ilike.%${t}%`, `species.ilike.%${t}%`, `variety.ilike.%${t}%`]
    if (/^\d+$/.test(t)) ors.push(`legacy_pos_inventory_code.eq.${t}`, `legacy_ims_inventory_code.eq.${t}`)
    q = supabase.from('products').select('*').eq('is_depleted', true).or(ors.join(',')).order('name').limit(limit)
  }
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Product[]
}

// Deplete / undeplete a product (legacy Inventory Depleted flag).
export async function setProductDepleted(id: string, depleted: boolean): Promise<void> {
  const { error } = await supabase.from('products').update({ is_depleted: depleted }).eq('id', id)
  if (error) throw error
}

// Flat invoice rows for accounting export (legacy Database Utilities
// Export_Invoices). Date range inclusive of start, exclusive of endExclusive.
export async function exportInvoicesRange(start: string, endExclusive: string): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('legacy_invoice_number, invoice_date, status, is_void, payment_method, subtotal, tax, total, total_qty, register_id, legacy_clerk_id, customers(name, company_name, first_name, last_name)')
    .gte('invoice_date', start)
    .lt('invoice_date', endExclusive)
    .order('invoice_date')
  if (error) throw error
  return (data ?? []).map(r => {
    const c = r.customers as { name?: string; company_name?: string; first_name?: string; last_name?: string } | null
    const customer = c?.company_name || c?.name || [c?.first_name, c?.last_name].filter(Boolean).join(' ') || 'Cash Sale'
    return {
      invoice_number: r.legacy_invoice_number,
      date: r.invoice_date,
      status: r.status,
      void: r.is_void ? 'Y' : 'N',
      customer,
      payment_method: r.payment_method,
      subtotal: r.subtotal,
      tax: r.tax,
      total: r.total,
      qty: r.total_qty,
      register: r.register_id,
      clerk_id: r.legacy_clerk_id,
    }
  })
}

// Invoice reconciliation (legacy frmInvoiceReconciliation, adapted to our data):
// compare each non-void invoice's total against the sum of its recorded payments
// over a date range, flagging unpaid / underpaid / overpaid / balanced.
export interface ReconRow {
  id: string
  invoice_number: number | null
  invoice_date: string | null
  status: string | null
  payment_method: string | null
  total: number
  paid: number
  diff: number
  flag: 'unpaid' | 'underpaid' | 'overpaid' | 'balanced'
}

export async function reconcileInvoices(start: string, endExclusive: string): Promise<ReconRow[]> {
  const { data: invs, error } = await supabase
    .from('invoices')
    .select('id, legacy_invoice_number, invoice_date, status, payment_method, total, total_price, tax')
    .gte('invoice_date', start).lt('invoice_date', endExclusive)
    .eq('is_void', false)
    .order('invoice_date')
  if (error) throw error
  const invoices = invs ?? []
  const ids = invoices.map(i => i.id as string)

  // Sum payments per invoice, chunking the id list to keep URLs sane.
  const paidByInvoice = new Map<string, number>()
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200)
    const { data: pays, error: pErr } = await supabase.from('payments').select('invoice_id, amount').in('invoice_id', chunk)
    if (pErr) throw pErr
    for (const p of pays ?? []) {
      const k = p.invoice_id as string
      paidByInvoice.set(k, (paidByInvoice.get(k) ?? 0) + Number(p.amount ?? 0))
    }
  }

  return invoices.map(inv => {
    // total_price = subtotal + tax (the full charged amount, matches payments.amount)
    // total alone = pre-tax subtotal in legacy data, so use total_price for comparison
    const total = Number((inv as Record<string, unknown>).total_price ?? inv.total ?? 0)
    const paid = paidByInvoice.get(inv.id as string) ?? 0
    const diff = Math.round((paid - total) * 100) / 100
    const flag: ReconRow['flag'] = paid === 0 && total !== 0 ? 'unpaid'
      : diff < -0.009 ? 'underpaid' : diff > 0.009 ? 'overpaid' : 'balanced'
    return {
      id: inv.id as string,
      invoice_number: (inv.legacy_invoice_number as number | null) ?? null,
      invoice_date: (inv.invoice_date as string | null) ?? null,
      status: (inv.status as string | null) ?? null,
      payment_method: (inv.payment_method as string | null) ?? null,
      total, paid, diff, flag,
    }
  })
}

// ---- App settings (key/value) — active inventory year, etc. -----------------
export async function getSetting(key: string): Promise<string | null> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', key).maybeSingle()
  if (error) throw error
  return (data?.value as string | null) ?? null
}

export async function setSetting(key: string, value: string): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' })
  if (error) throw error
}

// Distinct inventory types for the type filter.
export async function listInventoryTypes(): Promise<string[]> {
  const { data, error } = await supabase.from('products').select('inventory_type').eq('is_active', true).not('inventory_type', 'is', null)
  if (error) throw error
  return [...new Set((data ?? []).map(r => r.inventory_type as string).filter(Boolean))].sort()
}

export async function getProductCount(): Promise<number> {
  const { count, error } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
  if (error) throw error
  return count ?? 0
}

export function listProducts(opts: ListOptions = {}) {
  return queryList<Product>('products', opts, {
    searchCols: ['name', 'sku', 'scan_number'],
    numericCols: ['legacy_pos_inventory_code', 'legacy_ims_inventory_code'],
    defaultSort: { column: 'name', dir: 'asc' },
  })
}

// ---- Customers ---------------------------------------------------------------

export function listCustomers(opts: ListOptions = {}) {
  return queryList<Customer>('customers', opts, {
    searchCols: ['name', 'company_name', 'first_name', 'last_name', 'email', 'phone'],
    defaultSort: { column: 'name', dir: 'asc' },
    countMode: 'estimated',
    selectCols: 'id,name,company_name,first_name,last_name,email,phone,phone_home,phone_work,city,state,zip,legacy_ims_id,legacy_pos_id',
  })
}

export async function getCustomer(id: string): Promise<Customer | null> {
  const { data, error } = await supabase.from('customers').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return (data as Customer) ?? null
}

export async function getCustomerByLegacyImsId(legacyId: number): Promise<Customer | null> {
  const { data, error } = await supabase.from('customers').select('*').eq('legacy_ims_id', legacyId).maybeSingle()
  if (error) throw error
  return (data as Customer) ?? null
}

// Faithful port of Customer_Add.CheckDuplicateCustomer: match last name + address
// + city + state + zip (case-insensitive). Excludes the record being edited.
export async function checkDuplicateCustomer(
  fields: { last_name?: string | null; address?: string | null; city?: string | null; state?: string | null; zip?: string | null },
  excludeId?: string,
): Promise<boolean> {
  const ln = (fields.last_name ?? '').trim()
  if (!ln) return false
  let q = supabase.from('customers').select('id')
    .ilike('last_name', ln)
    .ilike('address', (fields.address ?? '').trim())
    .ilike('city', (fields.city ?? '').trim())
    .ilike('state', (fields.state ?? '').trim())
    .ilike('zip', (fields.zip ?? '').trim())
    .limit(1)
  if (excludeId) q = q.neq('id', excludeId)
  const { data, error } = await q
  if (error) throw error
  return (data?.length ?? 0) > 0
}

export async function createCustomer(c: Partial<Customer>): Promise<Customer> {
  const { data, error } = await supabase.from('customers').insert(c).select().single()
  if (error) throw error
  return data as Customer
}

export async function updateCustomer(id: string, c: Partial<Customer>): Promise<Customer> {
  const { data, error } = await supabase.from('customers').update(c).eq('id', id).select().single()
  if (error) throw error
  return data as Customer
}

// Adjust a customer's store-credit balance by delta (negative to deduct on a
// Store Credit tender; faithful to ProcessStoreCredit: Customer.fcBalance -= amt).
// Returns the new balance.
export async function adjustCustomerStoreCredit(customerId: string, delta: number): Promise<number> {
  const { data, error } = await supabase.from('customers').select('store_credit').eq('id', customerId).single()
  if (error) throw error
  const current = Number((data as { store_credit: number | null }).store_credit ?? 0)
  const next = Math.round((current + delta) * 100) / 100
  const { error: upErr } = await supabase.from('customers').update({ store_credit: next }).eq('id', customerId)
  if (upErr) throw upErr
  return next
}

// Adjust a customer's accounts-receivable balance by delta (positive to charge
// the house account on an In-House Charge tender). Returns the new balance.
export async function adjustCustomerArBalance(customerId: string, delta: number): Promise<number> {
  const { data, error } = await supabase.from('customers').select('balance').eq('id', customerId).single()
  if (error) throw error
  const current = Number((data as { balance: number | null }).balance ?? 0)
  const next = Math.round((current + delta) * 100) / 100
  const { error: upErr } = await supabase.from('customers').update({ balance: next }).eq('id', customerId)
  if (upErr) throw upErr
  return next
}

// Customer_View tabs: related estimates, job invoices (bills), appointments.
// These also match on the legacy IMS customer id (legacy_customer_id) as a
// safety net: the data migration didn't always backfill the new customer_id
// UUID (see migration 023), so some rows are linked only by legacy id.
function byCustomerOrLegacy(customerId: string, legacyId?: number | null): string {
  return legacyId != null
    ? `customer_id.eq.${customerId},legacy_customer_id.eq.${legacyId}`
    : `customer_id.eq.${customerId}`
}

export async function getCustomerEstimates(customerId: string, legacyImsId?: number | null): Promise<Estimate[]> {
  const { data, error } = await supabase.from('estimates')
    .select('*, designer:designers!designer_id(name)')
    .or(byCustomerOrLegacy(customerId, legacyImsId)).order('estimate_date', { ascending: false }).limit(200)
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, designer_name: r.designer?.name ?? null })) as Estimate[]
}

export async function getCustomerBills(customerId: string, legacyImsId?: number | null): Promise<Bill[]> {
  const { data, error } = await supabase.from('bills')
    .select('*, designer:designers!designer_id(name), foreman:workers!foreman_id(first_name, last_name)')
    .or(byCustomerOrLegacy(customerId, legacyImsId)).order('bill_date', { ascending: false }).limit(200)
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r,
    designer_name: r.designer?.name ?? null,
    foreman_name: r.foreman ? [r.foreman.last_name, r.foreman.first_name].filter(Boolean).join(', ') : null,
  })) as Bill[]
}

export async function getCustomerAppointments(customerId: string, legacyImsId?: number | null): Promise<Appointment[]> {
  const { data, error } = await supabase.from('appointments')
    .select('*, designer:designers!designer_id(name)')
    .or(byCustomerOrLegacy(customerId, legacyImsId)).order('appointment_date', { ascending: false }).limit(200)
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, designer_name: r.designer?.name ?? null })) as Appointment[]
}

// ---- Related customers (POS frmCustomerRelationship / tblRelatedCustomers) --
export interface RelatedCustomerLink { relationship_id: string; customer: Customer; description: string | null }

export async function listRelatedCustomers(customerId: string): Promise<RelatedCustomerLink[]> {
  // Relationships are bidirectional: this customer may be the main OR the related side.
  const [a, b] = await Promise.all([
    supabase.from('related_customers').select('id, description, other:customers!related_customer_id(*)').eq('main_customer_id', customerId),
    supabase.from('related_customers').select('id, description, other:customers!main_customer_id(*)').eq('related_customer_id', customerId),
  ])
  if (a.error) throw a.error
  if (b.error) throw b.error
  const out: RelatedCustomerLink[] = []
  for (const r of [...(a.data ?? []), ...(b.data ?? [])]) {
    if (r.other) out.push({ relationship_id: r.id as string, customer: r.other as unknown as Customer, description: (r.description as string | null) ?? null })
  }
  return out
}

export async function addRelatedCustomer(mainId: string, relatedId: string, description: string | null): Promise<void> {
  const { error } = await supabase.from('related_customers')
    .insert({ main_customer_id: mainId, related_customer_id: relatedId, description: description || null })
  if (error) throw error
}

export async function removeRelatedCustomer(relationshipId: string): Promise<void> {
  const { error } = await supabase.from('related_customers').delete().eq('id', relationshipId)
  if (error) throw error
}

// POS sales for a customer. Also matches the legacy POS customer id
// (legacy_customer_id) for rows the migration left without a customer_id UUID.
export async function getCustomerInvoices(customerId: string, legacyPosId?: number | null): Promise<Invoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .or(byCustomerOrLegacy(customerId, legacyPosId))
    .order('invoice_date', { ascending: false })
    .limit(100)
  if (error) throw error
  return (data ?? []) as Invoice[]
}

// ---- Invoices ----------------------------------------------------------------

export async function createInvoice(invoice: Partial<Invoice>, items: Partial<InvoiceItem>[], payment: Partial<Payment>): Promise<Invoice> {
  const { data: inv, error } = await supabase.from('invoices').insert(invoice).select().single()
  if (error) throw error
  const invoiceId = (inv as Invoice).id

  if (items.length) {
    const rows = items.map(i => ({ ...i, invoice_id: invoiceId }))
    const { error: itemErr } = await supabase.from('invoice_items').insert(rows)
    if (itemErr) throw itemErr
  }

  if (payment && payment.amount) {
    const { error: payErr } = await supabase.from('payments').insert({ ...payment, invoice_id: invoiceId })
    if (payErr) throw payErr
  }

  return inv as Invoice
}

// Faithful port of frmExpressInvoicing.GetInventoryItem: long (>9 char) codes are
// scanned barcodes; shorter codes are the numeric Inventory_Code. We match the
// barcode (scan_number/sku) AND the legacy POS/IMS inventory code columns, since
// the migrated data keeps Inventory_Code in legacy_pos/ims_inventory_code.
export async function lookupProductByCode(code: string): Promise<Product | null> {
  const c = code.trim()
  if (!c) return null
  const ors: string[] = [`scan_number.eq.${c}`, `sku.eq.${c}`]
  if (/^\d+$/.test(c)) {
    ors.push(`legacy_pos_inventory_code.eq.${c}`, `legacy_ims_inventory_code.eq.${c}`)
  }
  // Barcodes (>9 digits) are matched on scan_number only, matching legacy behavior.
  const filter = c.length > 9 ? `scan_number.eq.${c},sku.eq.${c}` : ors.join(',')
  const { data, error } = await supabase.from('products').select('*').or(filter).limit(1)
  if (error) throw error
  return (data?.[0] as Product) ?? null
}

// Create a completed sale with line items and one-or-more tender payments (split tender).
export async function createSale(
  invoice: Partial<Invoice>,
  items: Partial<InvoiceItem>[],
  payments: Partial<Payment>[],
): Promise<Invoice> {
  const { data: inv, error } = await supabase.from('invoices').insert(invoice).select().single()
  if (error) throw error
  const invoiceId = (inv as Invoice).id

  if (items.length) {
    const rows = items.map(i => ({ ...i, invoice_id: invoiceId }))
    const { error: itemErr } = await supabase.from('invoice_items').insert(rows)
    if (itemErr) throw itemErr
  }

  const payRows = payments.filter(p => p.amount).map(p => ({ ...p, invoice_id: invoiceId }))
  if (payRows.length) {
    const { error: payErr } = await supabase.from('payments').insert(payRows)
    if (payErr) throw payErr
  }

  return inv as Invoice
}

export async function getNextInvoiceNumber(): Promise<number> {
  const { data, error } = await supabase
    .from('invoices')
    .select('legacy_invoice_number')
    .order('legacy_invoice_number', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  const last = (data?.legacy_invoice_number as number) ?? 0
  return last + 1
}

export function listInvoices(opts: ListOptions = {}) {
  return queryList<Invoice>('invoices', opts, {
    numericCols: ['legacy_invoice_number'],
    defaultSort: { column: 'invoice_date', dir: 'desc' },
    countMode: 'estimated',
    // Header columns only (+ embedded customer name) so the grid loads fast; the
    // line-item detail is fetched lazily when a row is expanded.
    selectCols: 'id,legacy_invoice_number,invoice_date,clerk_id,legacy_clerk_id,register_id,customer_id,subtotal,tax,total,total_qty,payment_method,status,is_void,customers(name,company_name,first_name,last_name)',
  })
}

// Lazily load the line items for one invoice (the "rolled-up detail" shown when
// a transaction row is expanded in the dashboard).
export async function listInvoiceItems(invoiceId: string): Promise<InvoiceItem[]> {
  const { data, error } = await supabase
    .from('invoice_items')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as InvoiceItem[]
}

// ---- Clerk PIN gate (legacy frmLoginPopUp) -----------------------------------
// Clerks pick their name and enter their PIN before each transaction. The PIN
// *is* the clerk's auth password (migrated from POS1.tblLogins.fsPassword), so
// it is verified against Supabase Auth rather than stored in the clerks table.

export interface Clerk {
  id: string          // auth.users id -> stamped on invoices.clerk_id
  username: string
  email: string
  role: string
}

// Active clerks for the name picker (no PIN/secret exposed).
// Uses a SECURITY DEFINER RPC so it works on the login page before auth.
export async function listClerks(): Promise<Clerk[]> {
  const { data, error } = await supabase.rpc('list_active_clerks')
  if (error) throw error
  return (data ?? []).map((c: { id: string; username: string; email: string }) => ({
    id: c.id,
    username: c.username,
    email: c.email,
    role: '',
  }))
}

// ---- Clerk / user administration (legacy frmUsers + frmUserPermissions) ------
// A clerk's login is a Supabase Auth user (PIN = password). The clerks table is
// the directory; user_roles holds the role + granular permission flags. Creating
// users, resetting PINs and (de)activating require service-role, so those go
// through the manage-clerk edge function; role/permission edits use RLS directly.

export const CLERK_PERMISSIONS = [
  { key: 'perm_can_return', label: 'Process Returns', group: 'Transaction' },
  { key: 'perm_void_item', label: 'Void Item', group: 'Transaction' },
  { key: 'perm_void_transaction', label: 'Void Transaction', group: 'Transaction' },
  { key: 'perm_void_past_transactions', label: 'Void Past Transactions', group: 'Transaction' },
  { key: 'perm_paid_out', label: 'Paid Out / Payout', group: 'Transaction' },
  { key: 'perm_allow_drop', label: 'Allow Cash Drop', group: 'Transaction' },
  { key: 'perm_change_price_on_plu', label: 'Change Price on Item', group: 'Discounts' },
  { key: 'perm_percent_discount', label: 'Percent Discount', group: 'Discounts' },
  { key: 'perm_dollar_off_discount', label: 'Dollar-Off Discount', group: 'Discounts' },
  { key: 'perm_fixed_discount', label: 'Fixed Discount', group: 'Discounts' },
  { key: 'perm_sub_total_discount', label: 'Subtotal Discount', group: 'Discounts' },
  { key: 'perm_manager_discount', label: 'Manager Discount', group: 'Discounts' },
  { key: 'perm_cash_tender_discount', label: 'Cash Tender Discount', group: 'Discounts' },
  { key: 'perm_store_coupon', label: 'Store Coupon', group: 'Discounts' },
  { key: 'perm_view_customer_info', label: 'View Customer Info', group: 'Access' },
  { key: 'perm_reverse_training_sales', label: 'Reverse Training Sales', group: 'Access' },
  { key: 'perm_lotto_pay_out', label: 'Lotto Pay Out', group: 'Access' },
  { key: 'module_cashier', label: 'Cashier / Register', group: 'Modules' },
  { key: 'module_express_invoicing', label: 'Express Invoicing', group: 'Modules' },
  { key: 'module_customer_management', label: 'Customer Management', group: 'Modules' },
  { key: 'module_inventory_management', label: 'Inventory Management', group: 'Modules' },
  { key: 'module_purchasing_receiving', label: 'Purchasing / Receiving', group: 'Modules' },
  { key: 'module_vendor_management', label: 'Vendor Management', group: 'Modules' },
  { key: 'module_accounts_receivable', label: 'Accounts Receivable', group: 'Modules' },
  { key: 'module_reports', label: 'Reports', group: 'Modules' },
  { key: 'module_system_utilities', label: 'System Utilities', group: 'Modules' },
] as const

export const CLERK_AMOUNT_PERMISSIONS = [
  { key: 'perm_max_refund_amount', label: 'Max Refund Amount' },
  { key: 'perm_max_tender_amount', label: 'Max Tender Amount' },
] as const

export interface ClerkAdmin {
  id: string
  clerk_id: string | null
  user_id: string | null
  username: string
  email: string
  role: string
  is_active: boolean
  legacy_login_id: number | null
  perms: Record<string, boolean | number>
}

// Full clerk directory joined with permission flags for the admin screen.
export async function listClerkAdmins(): Promise<ClerkAdmin[]> {
  const [{ data: clerks, error: cErr }, { data: roles, error: rErr }] = await Promise.all([
    supabase.from('clerks').select('id, user_id, username, email, role, is_active, legacy_login_id').order('username'),
    supabase.from('user_roles').select('*'),
  ])
  if (cErr) throw cErr
  if (rErr) throw rErr
  const roleByUser = new Map((roles ?? []).map(r => [r.user_id as string, r as Record<string, unknown>]))
  return (clerks ?? []).map(c => {
    const ur = c.user_id ? roleByUser.get(c.user_id as string) : undefined
    const perms: Record<string, boolean | number> = {}
    if (ur) for (const k of Object.keys(ur)) if (k.startsWith('perm_') || k.startsWith('module_')) perms[k] = ur[k] as boolean | number
    return {
      id: c.id as string,
      clerk_id: c.id as string,
      user_id: (c.user_id as string | null) ?? null,
      username: c.username as string,
      email: c.email as string,
      role: c.role as string,
      is_active: Boolean(c.is_active),
      legacy_login_id: (c.legacy_login_id as number | null) ?? null,
      perms,
    }
  })
}

// Update a clerk's permission flags / limits (admin via RLS on user_roles).
export async function updateClerkPermissions(userId: string, perms: Record<string, boolean | number>): Promise<void> {
  const { error } = await supabase.from('user_roles').update(perms).eq('user_id', userId)
  if (error) throw error
}

export type ManageClerkAction = 'create' | 'update' | 'reset_pin' | 'set_active'

// Call the manage-clerk edge function for auth-level operations (service role).
export async function manageClerk(action: ManageClerkAction, payload: Record<string, unknown>): Promise<{ ok: boolean; user_id?: string; email?: string }> {
  const { data, error } = await supabase.functions.invoke('manage-clerk', { body: { action, ...payload } })
  if (error) {
    // Surface the function's JSON error message when present.
    const ctx = (error as { context?: { error?: string } }).context
    throw new Error(ctx?.error || error.message)
  }
  if (data && (data as { error?: string }).error) throw new Error((data as { error: string }).error)
  return data as { ok: boolean; user_id?: string; email?: string }
}

// Clerk-name lookups for the transactions grid. Resolves BOTH:
//   - byUuid:   invoices.clerk_id (UUID -> auth.users) via clerks.user_id, used
//               by web-created sales (the register stamps the verified clerk_id).
//   - byLegacy: invoices.legacy_clerk_id (integer) via clerks.legacy_login_id,
//               used by historic migrated invoices (legacy fnClerkID).
// Includes inactive clerks so old invoices still resolve. Small (~29 rows).
export async function getClerkNameMaps(): Promise<{
  byUuid: Record<string, string>
  byLegacy: Record<number, string>
}> {
  const { data, error } = await supabase
    .from('clerks')
    .select('user_id, legacy_login_id, username')
  if (error) throw error
  const byUuid: Record<string, string> = {}
  const byLegacy: Record<number, string> = {}
  for (const c of data ?? []) {
    if (c.user_id != null) byUuid[c.user_id as string] = c.username as string
    if (c.legacy_login_id != null) byLegacy[c.legacy_login_id as number] = c.username as string
  }
  return { byUuid, byLegacy }
}

// Verify a clerk's PIN by signing in on the isolated clerk-auth client (the PIN
// is the auth password). Returns the verified auth user id, or null on failure.
// The main app session is NOT affected.
export async function verifyClerkPin(email: string, pin: string): Promise<string | null> {
  try {
    const { data, error } = await clerkAuthClient.auth.signInWithPassword({ email, password: pin })
    const id = data?.user?.id ?? null
    await clerkAuthClient.auth.signOut().catch(() => {})
    if (error) return null
    return id
  } catch {
    return null
  }
}

// Manager-override check (legacy: a higher-privileged clerk authorizes a
// restricted action with their PIN). Signs in the overriding clerk on the
// isolated client, reads THEIR OWN user_roles row (RLS-safe), and confirms they
// hold the required permission (admins/managers always pass). The main app
// session is untouched. Returns the overriding clerk's name on success.
export async function verifyClerkOverride(
  email: string, pin: string, perm: string,
): Promise<{ ok: boolean; username?: string }> {
  try {
    const { data, error } = await clerkAuthClient.auth.signInWithPassword({ email, password: pin })
    if (error || !data?.user) { await clerkAuthClient.auth.signOut().catch(() => {}); return { ok: false } }
    const uid = data.user.id
    const { data: ur } = await clerkAuthClient
      .from('user_roles')
      .select(`role, ${perm}`)
      .eq('user_id', uid)
      .maybeSingle()
    await clerkAuthClient.auth.signOut().catch(() => {})
    const r = (ur as Record<string, unknown> | null)?.role
    const allowed = r === 'admin' || r === 'manager' || (ur as Record<string, unknown> | null)?.[perm] === true
    const username = (data.user.user_metadata?.username as string | undefined) ?? email
    return { ok: Boolean(allowed), username: allowed ? username : undefined }
  } catch {
    return { ok: false }
  }
}

// ---- In-progress / suspended transactions (legacy frmExpressInvoicing) -------
// The legacy POS persists the working cart to the DB immediately as an
// "In Progress" invoice and writes its items as they are added, so a crash
// leaves the transaction recoverable. Suspend sets status "Suspended"; closing
// mid-sale sets "Interrupted". All three are resumable.

// Create (when invoiceId is null) or update an in-progress invoice header and
// replace ALL of its line items. Returns the invoice id.
export async function saveDraftInvoice(
  invoiceId: string | null,
  header: Partial<Invoice>,
  items: Partial<InvoiceItem>[],
): Promise<string> {
  let id = invoiceId
  if (!id) {
    const { data, error } = await supabase.from('invoices').insert(header).select('id').single()
    if (error) throw error
    id = (data as { id: string }).id
  } else {
    const { error } = await supabase.from('invoices').update(header).eq('id', id)
    if (error) throw error
    const { error: delErr } = await supabase.from('invoice_items').delete().eq('invoice_id', id)
    if (delErr) throw delErr
  }
  if (items.length) {
    const rows = items.map(i => ({ ...i, invoice_id: id }))
    const { error } = await supabase.from('invoice_items').insert(rows)
    if (error) throw error
  }
  return id as string
}

export async function setInvoiceStatus(invoiceId: string, status: string): Promise<void> {
  const { error } = await supabase.from('invoices').update({ status }).eq('id', invoiceId)
  if (error) throw error
}

// Discard an abandoned draft (header + items + any staged payments).
export async function deleteInvoiceDraft(invoiceId: string): Promise<void> {
  await supabase.from('invoice_items').delete().eq('invoice_id', invoiceId)
  await supabase.from('payments').delete().eq('invoice_id', invoiceId)
  const { error } = await supabase.from('invoices').delete().eq('id', invoiceId)
  if (error) throw error
}

export interface ResumableInvoice {
  id: string
  legacy_invoice_number: number | null
  invoice_date: string | null
  status: string | null
  total: number | null
  total_qty: number | null
  customer_name: string | null
  register_id: number | null
}

// Suspended / Interrupted / In Progress invoices that can be recalled, newest
// first. registerNumber = 0 returns all lanes (matches the Z-Out ALL option).
export async function listResumableInvoices(registerNumber: number): Promise<ResumableInvoice[]> {
  let q = supabase.from('invoices')
    .select('id, legacy_invoice_number, invoice_date, status, total, total_qty, customer_name, register_id')
    .in('status', ['Suspended', 'Interrupted', 'In Progress'])
    .eq('is_void', false)
    .order('invoice_date', { ascending: false })
  if (registerNumber) q = q.eq('register_id', registerNumber)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as ResumableInvoice[]
}

// Complete an in-progress sale: upsert the header as Completed, replace items,
// and insert the tender payments. Returns the invoice id.
export async function finalizeDraftInvoice(
  invoiceId: string | null,
  header: Partial<Invoice>,
  items: Partial<InvoiceItem>[],
  payments: Partial<Payment>[],
): Promise<string> {
  const id = await saveDraftInvoice(invoiceId, header, items)
  const payRows = payments.filter(p => p.amount).map(p => ({ ...p, invoice_id: id }))
  if (payRows.length) {
    const { error } = await supabase.from('payments').insert(payRows)
    if (error) throw error
  }
  return id
}

export async function getInvoiceWithItems(id: string): Promise<{ invoice: Invoice; items: InvoiceItem[]; payments: Payment[] } | null> {
  const { data: invoice, error } = await supabase.from('invoices').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  if (!invoice) return null
  const { data: items } = await supabase.from('invoice_items').select('*').eq('invoice_id', id)
  const { data: payments } = await supabase.from('payments').select('*').eq('invoice_id', id)
  return {
    invoice: invoice as Invoice,
    items: (items ?? []) as InvoiceItem[],
    payments: (payments ?? []) as Payment[],
  }
}

// Void an invoice (faithful to InvoiceLookup VOID -> boolVoid = true): flags the
// header as void and marks its status accordingly. Returns the updated invoice.
export async function voidInvoice(id: string): Promise<Invoice> {
  const { data, error } = await supabase.from('invoices')
    .update({ is_void: true, status: 'Void' }).eq('id', id).select().single()
  if (error) throw error
  return data as Invoice
}

// ---- Refunds -----------------------------------------------------------------

export async function getInvoiceByNumber(legacyNumber: number): Promise<{ invoice: Invoice; items: InvoiceItem[] } | null> {
  const { data: invoice, error } = await supabase.from('invoices').select('*').eq('legacy_invoice_number', legacyNumber).maybeSingle()
  if (error) throw error
  if (!invoice) return null
  const { data: items } = await supabase.from('invoice_items').select('*').eq('invoice_id', (invoice as Invoice).id)
  return { invoice: invoice as Invoice, items: (items ?? []) as InvoiceItem[] }
}

export function listRefunds(opts: ListOptions = {}) {
  return queryList<Refund>('refunds', opts, {
    numericCols: ['legacy_invoice_number'],
    defaultSort: { column: 'refund_date', dir: 'desc' },
  })
}

export async function createRefund(refund: Partial<Refund>, items: Partial<RefundItem>[]): Promise<Refund> {
  const { data, error } = await supabase.from('refunds').insert(refund).select().single()
  if (error) throw error
  const refundId = (data as Refund).id
  if (items.length) {
    const rows = items.map(i => ({ ...i, refund_id: refundId }))
    const { error: itemErr } = await supabase.from('refund_items').insert(rows)
    if (itemErr) throw itemErr
  }
  return data as Refund
}

// ---- Registers (DB-backed lanes; reader linked to register number) ----------

export async function listRegisters(activeOnly = false): Promise<Register[]> {
  let q = supabase.from('registers').select('*').order('register_number', { ascending: true })
  if (activeOnly) q = q.eq('is_active', true)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Register[]
}

export async function getRegisterByNumber(registerNumber: number): Promise<Register | null> {
  const { data, error } = await supabase.from('registers').select('*').eq('register_number', registerNumber).maybeSingle()
  if (error) throw error
  return (data as Register | null) ?? null
}

export async function createRegister(reg: Partial<Register>): Promise<Register> {
  const { data, error } = await supabase.from('registers').insert(reg).select().single()
  if (error) throw error
  return data as Register
}

export async function updateRegister(id: string, patch: Partial<Register>): Promise<Register> {
  const { data, error } = await supabase.from('registers').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id).select().single()
  if (error) throw error
  return data as Register
}

export async function deleteRegister(id: string): Promise<void> {
  const { error } = await supabase.from('registers').delete().eq('id', id)
  if (error) throw error
}

// ---- Register sessions (cash-drawer open/close) -----------------------------

export async function getOpenRegisterSession(registerNumber: number): Promise<RegisterSession | null> {
  const { data, error } = await supabase.from('register_sessions')
    .select('*').eq('register_number', registerNumber).eq('status', 'open')
    .order('opened_at', { ascending: false }).limit(1).maybeSingle()
  if (error) throw error
  return (data as RegisterSession | null) ?? null
}

export async function openRegisterSession(args: { register_number: number; opened_by?: string | null; starting_cash: number }): Promise<RegisterSession> {
  const { data, error } = await supabase.from('register_sessions').insert({
    register_number: args.register_number, opened_by: args.opened_by ?? null,
    starting_cash: args.starting_cash, status: 'open',
  }).select().single()
  if (error) throw error
  return data as RegisterSession
}

// Expected cash for an open session = starting cash + cash sales − cash payouts
// on that lane since it was opened. Used to compute over/short on a blind count.
export async function computeDrawerExpected(session: RegisterSession): Promise<{ cashSales: number; payoutsTotal: number; expected: number }> {
  const [{ data: sales }, { data: payouts }] = await Promise.all([
    supabase.from('invoices').select('total')
      .eq('register_id', session.register_number).eq('payment_method', 'Cash').eq('is_void', false)
      .gte('invoice_date', session.opened_at),
    supabase.from('payouts').select('amount, register_number, issue_date')
      .gte('issue_date', session.opened_at),
  ])
  const cashSales = (sales ?? []).reduce((s, r) => s + Number(r.total ?? 0), 0)
  const payoutsTotal = (payouts ?? [])
    .filter(p => p.register_number == null || String(p.register_number) === String(session.register_number))
    .reduce((s, p) => s + Number(p.amount ?? 0), 0)
  const expected = Number(session.starting_cash ?? 0) + cashSales - payoutsTotal
  return { cashSales, payoutsTotal, expected: Math.round(expected * 100) / 100 }
}

export async function closeRegisterSession(session: RegisterSession, args: { counted_cash: number; closed_by?: string | null; notes?: string | null }): Promise<RegisterSession> {
  const { cashSales, payoutsTotal, expected } = await computeDrawerExpected(session)
  const overShort = Math.round((args.counted_cash - expected) * 100) / 100
  const { data, error } = await supabase.from('register_sessions').update({
    status: 'closed', closed_by: args.closed_by ?? null, closed_at: new Date().toISOString(),
    counted_cash: args.counted_cash, cash_sales: cashSales, payouts_total: payoutsTotal,
    expected_cash: expected, over_short: overShort, notes: args.notes ?? null,
  }).eq('id', session.id).select().single()
  if (error) throw error
  return data as RegisterSession
}

export interface RegisterZoutRow { payment_method: string; payment_count: number; total_amount: number }
export interface RegisterDayTotals { gross_sales: number; total_tax: number; invoice_count: number }

export async function registerZout(day: string, registerNumber: number): Promise<RegisterZoutRow[]> {
  const { data, error } = await supabase.rpc('report_register_zout', { target_day: day, p_register: registerNumber })
  if (error) throw error
  return (data ?? []) as RegisterZoutRow[]
}

export async function registerDayTotals(day: string, registerNumber: number): Promise<RegisterDayTotals> {
  const { data, error } = await supabase.rpc('report_register_day_totals', { target_day: day, p_register: registerNumber })
  if (error) throw error
  const rows = (data ?? []) as RegisterDayTotals[]
  return rows[0] ?? { gross_sales: 0, total_tax: 0, invoice_count: 0 }
}

// Faithful legacy Z-Out (ZOutDiag) reconciliation. register = 0 -> ALL lanes.
export interface ZoutLine { label: string; count: number; amount: number; refund: number }
export interface ZoutData {
  day: string
  register: string
  payments: ZoutLine[]
  payouts: { count: number; amount: number; refund: number }
  register_total: { count: number; amount: number; refund: number }
  taxable_sales: number
  nontax_sales: number
  net_sales: number
  tax_total: number
  total: number
  voids: number
  management_voids: number
  refund_count: number
  refund_amount: number
  check_change_count: number
  check_change_amount: number
  suspended: number
  inprogress_count: number
  inprogress_amount: number
  departments: { name: string; amount: number }[]
}

export async function zoutReport(startDate: string, endDate: string, register: number): Promise<ZoutData> {
  const { data, error } = await supabase.rpc('report_zout', { start_date: startDate, end_date: endDate, p_register: register })
  if (error) throw error
  return data as ZoutData
}

// ---- Estimates / Bills / Appointments ---------------------------------------

export async function getEstimateWithItems(id: string): Promise<{ estimate: Estimate; items: EstimateItem[]; customer: Customer | null } | null> {
  const { data: estimate, error } = await supabase.from('estimates').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  if (!estimate) return null
  const { data: items } = await supabase
    .from('estimate_items')
    .select('*')
    .eq('estimate_id', id)
    .order('legacy_id', { ascending: true })
  let customer: Customer | null = null
  const est = estimate as Estimate
  if (est.customer_id) {
    customer = await getCustomer(est.customer_id)
  } else if (est.legacy_customer_id) {
    customer = await getCustomerByLegacyImsId(est.legacy_customer_id)
  }
  return { estimate: est, items: (items ?? []) as EstimateItem[], customer }
}

export function listEstimates(opts: ListOptions = {}) {
  return queryList<Estimate>('estimates', opts, {
    searchCols: ['bill_code', 'customer_name', 'designer_name'],
    numericCols: ['legacy_id'],
    defaultSort: { column: 'estimate_date', dir: 'desc' },
    countMode: 'estimated',
    // List grid + sort only need these; the editor refetches the full row.
    selectCols: 'id,legacy_id,bill_code,customer_name,designer_name,estimate_date,estimate_total,invoice_number',
  })
}

// Next estimate number (Estimate_Add assigns a sequential bill code). We keep
// numbering monotonic over both legacy and web-created rows by tracking the max
// legacy_id and writing it back on new estimates.
export async function getNextEstimateNumber(): Promise<number> {
  const { data, error } = await supabase
    .from('estimates')
    .select('legacy_id')
    .order('legacy_id', { ascending: false, nullsFirst: false })
    .limit(1)
  if (error) throw error
  const max = (data?.[0] as { legacy_id: number | null } | undefined)?.legacy_id ?? 0
  return (max || 0) + 1
}

// Create or update an estimate together with its line items. On update we
// replace the full item set (faithful to Estimate_Add, which rewrites items on
// save). Items are renumbered by position so the read order is preserved.
export async function saveEstimate(args: {
  id?: string
  estimate: Partial<Estimate>
  items: Partial<EstimateItem>[]
}): Promise<Estimate> {
  const { id, estimate, items } = args

  let saved: Estimate
  if (id) {
    const { data, error } = await supabase.from('estimates').update(estimate).eq('id', id).select().single()
    if (error) throw error
    saved = data as Estimate
    const { error: delErr } = await supabase.from('estimate_items').delete().eq('estimate_id', id)
    if (delErr) throw delErr
  } else {
    const { data, error } = await supabase.from('estimates').insert(estimate).select().single()
    if (error) throw error
    saved = data as Estimate
  }

  if (items.length) {
    const rows = items.map((it, idx) => ({ ...it, estimate_id: saved.id, legacy_id: idx + 1 }))
    const { error: itemErr } = await supabase.from('estimate_items').insert(rows)
    if (itemErr) throw itemErr
  }
  return saved
}

export async function deleteEstimate(id: string): Promise<void> {
  await supabase.from('estimate_items').delete().eq('estimate_id', id)
  const { error } = await supabase.from('estimates').delete().eq('id', id)
  if (error) throw error
}

export async function getBillWithItems(id: string): Promise<{ bill: Bill; items: BillItem[]; labor: LaborHour[]; customer: Customer | null } | null> {
  const { data: bill, error } = await supabase.from('bills').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  if (!bill) return null
  const { data: items } = await supabase.from('bill_items').select('*').eq('bill_id', id).order('legacy_id', { ascending: true })
  const { data: labor } = await supabase.from('labor_hours').select('*').eq('bill_id', id)
  let customer: Customer | null = null
  const b = bill as Bill
  if (b.customer_id) {
    customer = await getCustomer(b.customer_id)
  } else if (b.legacy_customer_id) {
    customer = await getCustomerByLegacyImsId(b.legacy_customer_id as number)
  }
  return {
    bill: b,
    items: (items ?? []) as BillItem[],
    labor: (labor ?? []) as LaborHour[],
    customer,
  }
}

export function listBills(opts: ListOptions = {}) {
  return queryList<Bill>('bills', opts, {
    searchCols: ['bill_code', 'customer_name'],
    defaultSort: { column: 'bill_date', dir: 'desc' },
  })
}

export async function getNextBillNumber(): Promise<number> {
  const { data, error } = await supabase
    .from('bills')
    .select('legacy_bill_id')
    .order('legacy_bill_id', { ascending: false, nullsFirst: false })
    .limit(1)
  if (error) throw error
  const max = (data?.[0] as { legacy_bill_id: number | null } | undefined)?.legacy_bill_id ?? 0
  return (max || 0) + 1
}

// ---- Job costing: workers, labor hours, overhead, actual (completion) items --
// Faithful port of IMS Invoice_WorkerHours / Invoice_Overhead /
// Invoice_JobCompletion, which were entry forms missing from the new app.

export async function listWorkers(activeOnly = true): Promise<Worker[]> {
  let q = supabase.from('workers').select('*').order('last_name').order('first_name')
  if (activeOnly) q = q.eq('is_active', true)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Worker[]
}

export async function saveWorker(w: Partial<Worker> & { id?: string }): Promise<Worker> {
  if (w.id) {
    const { id, ...rest } = w
    const { data, error } = await supabase.from('workers').update(rest).eq('id', id).select().single()
    if (error) throw error
    return data as Worker
  }
  const { data, error } = await supabase.from('workers').insert(w).select().single()
  if (error) throw error
  return data as Worker
}

// Recompute a bill's actual_cost & gross_profit from its actual (job-completion)
// cost rows + labor hours. Mirrors BillDetail's display math so stored totals
// stay consistent: actual_cost = sum(actual item cost*qty) + sum(labor rate*hrs).
export async function recomputeBillJobCosting(billId: string): Promise<void> {
  const [{ data: bill }, { data: items }, { data: labor }] = await Promise.all([
    supabase.from('bills').select('invoice_total, sales_tax_amount').eq('id', billId).maybeSingle(),
    supabase.from('bill_items').select('cost, qty, is_actual').eq('bill_id', billId),
    supabase.from('labor_hours').select('rate, hours').eq('bill_id', billId),
  ])
  if (!bill) return
  const actualItems = (items ?? []).filter(i => i.is_actual)
  const itemCost = actualItems.reduce((s, i) => s + Number(i.cost ?? 0) * Number(i.qty ?? 0), 0)
  const laborCost = (labor ?? []).reduce((s, l) => s + Number(l.rate ?? 0) * Number(l.hours ?? 0), 0)
  const actualCost = itemCost + laborCost
  const grossProfit = Number(bill.invoice_total ?? 0) - Number(bill.sales_tax_amount ?? 0) - actualCost
  const { error } = await supabase.from('bills').update({ actual_cost: actualCost, gross_profit: grossProfit }).eq('id', billId)
  if (error) throw error
}

export async function listLaborHours(billId: string): Promise<LaborHour[]> {
  const { data, error } = await supabase.from('labor_hours').select('*').eq('bill_id', billId).order('created_at')
  if (error) throw error
  return (data ?? []) as LaborHour[]
}

export async function addLaborHour(args: { bill_id: string; bill_code?: string | null; worker_id?: string | null; legacy_worker_id?: number | null; rate: number; hours: number }): Promise<void> {
  const { error } = await supabase.from('labor_hours').insert({
    bill_id: args.bill_id, bill_code: args.bill_code ?? null,
    worker_id: args.worker_id ?? null, legacy_worker_id: args.legacy_worker_id ?? null,
    rate: args.rate, hours: args.hours,
  })
  if (error) throw error
  await recomputeBillJobCosting(args.bill_id)
}

export async function updateLaborHour(id: string, billId: string, args: { worker_id?: string | null; rate: number; hours: number }): Promise<void> {
  const { error } = await supabase.from('labor_hours').update({ worker_id: args.worker_id ?? null, rate: args.rate, hours: args.hours }).eq('id', id)
  if (error) throw error
  await recomputeBillJobCosting(billId)
}

export async function deleteLaborHour(id: string, billId: string): Promise<void> {
  const { error } = await supabase.from('labor_hours').delete().eq('id', id)
  if (error) throw error
  await recomputeBillJobCosting(billId)
}

export interface OverheadRates {
  labor_oh: number; other_oh: number; cost_percentage_oh: number; burden_rate_oh: number; indirect_ir_oh: number
}

export async function saveBillOverhead(billId: string, rates: OverheadRates): Promise<void> {
  const { error } = await supabase.from('bills').update(rates).eq('id', billId)
  if (error) throw error
}

// Replace the actual (job-completion) cost rows on a bill, then recompute.
export async function saveActualItems(billId: string, billCode: string | null, rows: Array<{ item_name: string; item_type: string | null; qty: number; cost: number }>): Promise<void> {
  const { error: delErr } = await supabase.from('bill_items').delete().eq('bill_id', billId).eq('is_actual', true)
  if (delErr) throw delErr
  if (rows.length) {
    const insert = rows.map(r => ({
      bill_id: billId, bill_code: billCode, is_actual: true,
      item_name: r.item_name, item_type: r.item_type, qty: r.qty, cost: r.cost,
      price: 0, total: 0,
    }))
    const { error: insErr } = await supabase.from('bill_items').insert(insert)
    if (insErr) throw insErr
  }
  await recomputeBillJobCosting(billId)
}

// Create or update a Job Invoice (Bill) with its billed line items. On update we
// replace only the billed items (is_actual = false/null); actual-cost rows and
// labor_hours used for job costing are preserved.
export async function saveBill(args: {
  id?: string
  bill: Partial<Bill>
  items: Partial<BillItem>[]
}): Promise<Bill> {
  const { id, bill, items } = args

  let saved: Bill
  if (id) {
    const { data, error } = await supabase.from('bills').update(bill).eq('id', id).select().single()
    if (error) throw error
    saved = data as Bill
    const { error: delErr } = await supabase
      .from('bill_items')
      .delete()
      .eq('bill_id', id)
      .or('is_actual.is.null,is_actual.eq.false')
    if (delErr) throw delErr
  } else {
    const { data, error } = await supabase.from('bills').insert(bill).select().single()
    if (error) throw error
    saved = data as Bill
  }

  if (items.length) {
    const rows = items.map((it, idx) => ({ ...it, bill_id: saved.id, is_actual: false, legacy_id: idx + 1 }))
    const { error: itemErr } = await supabase.from('bill_items').insert(rows)
    if (itemErr) throw itemErr
  }
  return saved
}

// Find a Bill already created from a given estimate (job code link), if any.
export async function getBillByEstimate(estimateId: string): Promise<Bill | null> {
  const { data, error } = await supabase
    .from('bills')
    .select('*')
    .eq('estimate_id', estimateId)
    .order('bill_date', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as Bill | null) ?? null
}

// Faithful port of the IMS "Make Bill" action: copies an estimate (header +
// line items) into a new Job Invoice (Bill), mapping estimate_* totals/discounts
// onto the bill's invoice_* fields, linking back via estimate_id, and stamping
// the resulting bill number onto the estimate's invoice_number. Billed items are
// committed (is_actual = false) so the actual-cost rows can be entered later.
export async function convertEstimateToBill(estimateId: string): Promise<Bill> {
  const res = await getEstimateWithItems(estimateId)
  if (!res) throw new Error('Estimate not found')
  const { estimate, items, customer } = res

  // Idempotency: if a bill already exists for this estimate, return it.
  const existing = await getBillByEstimate(estimateId)
  if (existing) return existing

  const legacyBillId = await getNextBillNumber()
  const taxAmt = estimate.sales_tax_amount ?? 0
  const cost = estimate.estimate_cost ?? items.reduce((s, i) => s + (i.cost ?? 0) * (i.qty ?? 0), 0)
  const total = estimate.estimate_total ?? 0

  const bill: Partial<Bill> = {
    legacy_bill_id: legacyBillId,
    bill_code: estimate.bill_code ?? String(legacyBillId),
    customer_id: estimate.customer_id,
    legacy_customer_id: estimate.legacy_customer_id,
    customer_name: customer ? customerLabel(customer) : estimate.customer_name ?? null,
    estimate_id: estimate.id,
    bill_date: new Date().toISOString(),
    is_non_taxable: estimate.is_non_taxable,
    comments: estimate.comments,
    is_actual: false,
    // Discounts: estimate per-type/whole -> bill inv_* fields
    inv_plant_discount_p: estimate.plant_discount_p,
    inv_plant_discount_d: estimate.plant_discount_d,
    inv_material_discount_p: estimate.material_discount_p,
    inv_material_discount_d: estimate.material_discount_d,
    inv_labor_discount_p: estimate.labor_discount_p,
    inv_labor_discount_d: estimate.labor_discount_d,
    rental_discount: estimate.rental_discount,
    rental_discount_percent: estimate.rental_discount_percent,
    whole_invoice_discount_p: estimate.whole_estimate_discount_p,
    whole_invoice_discount_d: estimate.whole_estimate_discount_d,
    // Totals
    plant_total: estimate.plant_total,
    disc_plant_total: estimate.disc_plant_total,
    material_total: estimate.material_total,
    disc_material_total: estimate.disc_material_total,
    labor_total: estimate.labor_total,
    disc_labor_total: estimate.disc_labor_total,
    rental_total: estimate.rental_total,
    disc_rental_total: estimate.disc_rental_total,
    invoice_sub_total: estimate.estimate_sub_total,
    whole_invoice: estimate.whole_estimate,
    disc_whole_invoice: estimate.disc_whole_estimate,
    invoice_total: total,
    sales_tax_percent: estimate.sales_tax_percent,
    sales_tax_amount: taxAmt,
    remaining_balance: estimate.remaining_balance,
    actual_cost: cost,
    gross_profit: total - taxAmt - cost,
    deposit: estimate.deposit,
    deposit_info: estimate.deposit_info,
  }

  const billItems: Partial<BillItem>[] = items.map(it => ({
    legacy_inventory_code: it.legacy_inventory_code,
    item_name: it.item_name,
    item_size: it.item_size,
    item_type: it.item_type,
    qty: it.qty,
    price: it.price,
    total: it.total,
    cost: it.cost,
    discount_percent: it.discount_percent,
    discount_dollar: it.discount_dollar,
    is_committed: true,
  }))

  const saved = await saveBill({ bill, items: billItems })

  // Stamp the resulting bill number back onto the estimate (marks as converted).
  await supabase.from('estimates').update({ invoice_number: saved.bill_code ?? String(legacyBillId) }).eq('id', estimate.id)

  return saved
}

export async function getAppointment(id: string): Promise<{ appointment: Appointment; customer: Customer | null } | null> {
  const { data, error } = await supabase.from('appointments')
    .select('*, designer:designers!designer_id(name)').eq('id', id).maybeSingle()
  if (error) throw error
  if (!data) return null
  const appointment = { ...data, designer_name: (data as any).designer?.name ?? null } as Appointment
  let customer: Customer | null = null
  if (appointment.customer_id) {
    customer = await getCustomer(appointment.customer_id)
  } else if (appointment.legacy_customer_id) {
    customer = await getCustomerByLegacyImsId(appointment.legacy_customer_id as number)
  }
  return { appointment, customer }
}

export function listAppointments(opts: ListOptions = {}) {
  return queryList<Appointment>('appointments', opts, {
    // List grid + sort only need these columns; the detail page refetches.
    selectCols: 'id, legacy_id, customer_name, designer_name, appointment_date, comments, go_ahead',
    searchCols: ['comments', 'outcome', 'customer_name', 'designer_name'],
    numericCols: ['legacy_id'],
    defaultSort: { column: 'appointment_date', dir: 'desc' },
    countMode: 'estimated',
  })
}

// Appointment create/edit support (IMS Appointment_Add / Appointment_View).
export interface DesignerOption { id: string; name: string }

export async function listDesigners(): Promise<DesignerOption[]> {
  const { data, error } = await supabase.from('designers').select('id, name')
    .eq('is_removed', false).order('name')
  if (error) throw error
  return (data ?? []) as DesignerOption[]
}

export async function listAppointmentOutcomes(): Promise<string[]> {
  const { data, error } = await supabase.from('appointment_outcomes').select('outcome').order('outcome')
  if (error) throw error
  return (data ?? []).map(r => r.outcome as string).filter(Boolean)
}

export async function createAppointment(a: Partial<Appointment>): Promise<Appointment> {
  const { data, error } = await supabase.from('appointments').insert(a).select().single()
  if (error) throw error
  return data as Appointment
}

export async function updateAppointment(id: string, a: Partial<Appointment>): Promise<Appointment> {
  const { data, error } = await supabase.from('appointments')
    .update({ ...a, updated_at: new Date().toISOString() }).eq('id', id).select().single()
  if (error) throw error
  return data as Appointment
}

// ---- Vendors -----------------------------------------------------------------

export function listVendors(opts: ListOptions = {}) {
  return queryList<Vendor>('vendors', opts, {
    searchCols: ['name', 'vendor_code', 'email', 'phone'],
    defaultSort: { column: 'name', dir: 'asc' },
  })
}

export async function createVendor(v: Partial<Vendor>): Promise<Vendor> {
  const { data, error } = await supabase.from('vendors').insert(v).select().single()
  if (error) throw error
  return data as Vendor
}

export async function updateVendor(id: string, v: Partial<Vendor>): Promise<Vendor> {
  const { data, error } = await supabase.from('vendors').update(v).eq('id', id).select().single()
  if (error) throw error
  return data as Vendor
}

// ---- Rolodex contacts (IMS New_Rolodex_Dashboard) ---------------------------

export function listRolodexContacts(opts: ListOptions = {}) {
  return queryList<RolodexContact>('rolodex_contacts', opts, {
    searchCols: ['last_name', 'first_name', 'company', 'city', 'state', 'zip', 'phone', 'notes'],
    defaultSort: { column: 'company', dir: 'asc' },
  })
}

export async function createRolodexContact(c: Partial<RolodexContact>): Promise<RolodexContact> {
  const { data, error } = await supabase.from('rolodex_contacts')
    .insert({ ...c, entry_date: new Date().toISOString() }).select().single()
  if (error) throw error
  return data as RolodexContact
}

export async function updateRolodexContact(id: string, c: Partial<RolodexContact>): Promise<RolodexContact> {
  const { data, error } = await supabase.from('rolodex_contacts').update(c).eq('id', id).select().single()
  if (error) throw error
  return data as RolodexContact
}

export async function deleteRolodexContact(id: string): Promise<void> {
  const { error } = await supabase.from('rolodex_contacts').delete().eq('id', id)
  if (error) throw error
}

// ---- Scrapped inventory (POS ScrappedInventoryUC) ---------------------------
// Records damage/loss per product and adjusts on-hand quantity, faithful to the
// legacy form which decremented Qty_Remaining when an item was scrapped.
export async function listScrappedInventory(productId: string): Promise<ScrappedInventory[]> {
  const { data, error } = await supabase.from('scrapped_inventory').select('*')
    .eq('product_id', productId).order('scrapped_on', { ascending: false })
  if (error) throw error
  return (data ?? []) as ScrappedInventory[]
}

export async function addScrappedInventory(args: { product_id: string; quantity: number; reason: string; scrapped_by: string; scrapped_on: string }): Promise<ScrappedInventory> {
  const now = new Date().toISOString()
  const { data, error } = await supabase.from('scrapped_inventory').insert({
    product_id: args.product_id, quantity: args.quantity, reason: args.reason || null,
    scrapped_by: args.scrapped_by || null, scrapped_on: args.scrapped_on, created_on: now,
  }).select().single()
  if (error) throw error
  // Decrement on-hand and bump the scrapped tally (faithful side-effect).
  const { data: p } = await supabase.from('products')
    .select('qty_remaining, qty_scrapped').eq('id', args.product_id).maybeSingle()
  if (p) {
    await supabase.from('products').update({
      qty_remaining: Number(p.qty_remaining ?? 0) - args.quantity,
      qty_scrapped: Number(p.qty_scrapped ?? 0) + args.quantity,
    }).eq('id', args.product_id)
  }
  return data as ScrappedInventory
}

export async function deleteScrappedInventory(row: ScrappedInventory): Promise<void> {
  const { error } = await supabase.from('scrapped_inventory').delete().eq('id', row.id)
  if (error) throw error
  // Restore the quantity that was removed when scrapped.
  if (row.product_id && row.quantity) {
    const { data: p } = await supabase.from('products')
      .select('qty_remaining, qty_scrapped').eq('id', row.product_id).maybeSingle()
    if (p) {
      await supabase.from('products').update({
        qty_remaining: Number(p.qty_remaining ?? 0) + Number(row.quantity),
        qty_scrapped: Math.max(0, Number(p.qty_scrapped ?? 0) - Number(row.quantity)),
      }).eq('id', row.product_id)
    }
  }
}

// ---- Product price/qty breakdown (POS frmLarryReport) -----------------------
// For one product over a year, the qty sold at each distinct price, by month.
export interface ProductPriceBreakdownRow { month: number; price: number; qty: number }

export async function productPriceBreakdown(productId: string, year: number): Promise<ProductPriceBreakdownRow[]> {
  const start = `${year}-01-01`
  const end = `${year}-12-31T23:59:59`
  const agg = new Map<string, ProductPriceBreakdownRow>()
  const PAGE = 1000
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase.from('invoice_items')
      .select('price, qty, invoices!inner(invoice_date, status, is_void)')
      .eq('product_id', productId)
      .eq('invoices.status', 'Complete')
      .eq('invoices.is_void', false)
      .gte('invoices.invoice_date', start)
      .lte('invoices.invoice_date', end)
      .range(from, from + PAGE - 1)
    if (error) throw error
    const rows = (data ?? []) as unknown as { price: number | null; qty: number | null; invoices: { invoice_date: string | null } }[]
    for (const r of rows) {
      const d = r.invoices?.invoice_date
      if (!d) continue
      const month = new Date(d).getMonth() + 1
      const price = Math.round(Number(r.price ?? 0) * 100) / 100
      const key = `${month}|${price}`
      const cur = agg.get(key) ?? { month, price, qty: 0 }
      cur.qty += Number(r.qty ?? 0)
      agg.set(key, cur)
    }
    if (rows.length < PAGE) break
  }
  return [...agg.values()].sort((a, b) => a.month - b.month || a.price - b.price)
}

// ---- Gift Certificates -------------------------------------------------------

export function listGiftCertificates(opts: ListOptions = {}) {
  return queryList<GiftCertificate>('gift_certificates', opts, {
    searchCols: ['certificate_number', 'customer_name', 'gift_for'],
    defaultSort: { column: 'issue_date', dir: 'desc' },
  })
}

export async function lookupGiftCertificate(certNumber: string): Promise<GiftCertificate | null> {
  // Strip swipe framing like the legacy GiftCertificate_Click (%...?).
  const clean = certNumber.replace(/%/g, '').split('?')[0].trim()
  const { data, error } = await supabase.from('gift_certificates').select('*').eq('certificate_number', clean).maybeSingle()
  if (error) throw error
  return (data as GiftCertificate) ?? null
}

// Issue a new gift certificate (faithful to frmGiftCertificates.Save for a new
// record): a customer is required; original_amount mirrors the opening balance.
export async function createGiftCertificate(gc: Partial<GiftCertificate>): Promise<GiftCertificate> {
  const { data, error } = await supabase.from('gift_certificates').insert(gc).select().single()
  if (error) throw error
  return data as GiftCertificate
}

export async function updateGiftCertificate(id: string, gc: Partial<GiftCertificate>): Promise<GiftCertificate> {
  const { data, error } = await supabase.from('gift_certificates').update(gc).eq('id', id).select().single()
  if (error) throw error
  return data as GiftCertificate
}

// Decrement a gift certificate's balance (faithful to ProcessGiftCard:
// tmpGC.fcAmount -= amountTendered). Returns the new balance.
export async function redeemGiftCertificate(certId: string, amount: number): Promise<number> {
  const { data, error } = await supabase.from('gift_certificates').select('current_balance').eq('id', certId).single()
  if (error) throw error
  const current = Number((data as { current_balance: number | null }).current_balance ?? 0)
  const next = Math.round((current - amount) * 100) / 100
  const { error: upErr } = await supabase.from('gift_certificates').update({ current_balance: next }).eq('id', certId)
  if (upErr) throw upErr
  return next
}

// ---- Payouts -----------------------------------------------------------------

export function listPayouts(opts: ListOptions = {}) {
  return queryList<Payout>('payouts', opts, {
    searchCols: ['detail'],
    defaultSort: { column: 'issue_date', dir: 'desc' },
    selectCols: 'id,legacy_id,issue_date,invoice_date,detail,amount,register_number,issued_from_id,legacy_issued_from_id,is_active',
  })
}

export async function createPayout(p: Partial<Payout>): Promise<Payout> {
  const { data, error } = await supabase.from('payouts').insert(p).select().single()
  if (error) throw error
  return data as Payout
}

// Edit an existing payout (admin/manager only — enforced by RLS + UI gating).
export async function updatePayout(id: string, p: Partial<Payout>): Promise<Payout> {
  const { data, error } = await supabase.from('payouts').update(p).eq('id', id).select().single()
  if (error) throw error
  return data as Payout
}

// ---- Receiving ---------------------------------------------------------------

export function listReceiving(opts: ListOptions = {}) {
  return queryList<Receiving>('receiving_headers', opts, {
    searchCols: ['pack_slip_number', 'vendor_name', 'shipment_number'],
    defaultSort: { column: 'receive_date', dir: 'desc' },
  })
}

export async function getReceivingDetail(headerId: string): Promise<ReceivingDetailItem[]> {
  const { data, error } = await supabase.from('receiving_detail').select('*').eq('receiving_header_id', headerId).order('order_number')
  if (error) throw error
  return (data ?? []) as ReceivingDetailItem[]
}

// Increment a product's on-hand and received quantities by qty (faithful to
// Received_Inventory: QtyReceived/QtyRemaining grow as stock is received).
export async function adjustProductQuantity(productId: string, qty: number): Promise<void> {
  const { data, error } = await supabase.from('products')
    .select('qty_on_hand, qty_received, qty_remaining').eq('id', productId).single()
  if (error) throw error
  const row = data as { qty_on_hand: number | null; qty_received: number | null; qty_remaining: number | null }
  const { error: upErr } = await supabase.from('products').update({
    qty_on_hand: Number(row.qty_on_hand ?? 0) + qty,
    qty_received: Number(row.qty_received ?? 0) + qty,
    qty_remaining: Number(row.qty_remaining ?? 0) + qty,
  }).eq('id', productId)
  if (upErr) throw upErr
}

// Create a receiving record (header + detail) and add the received quantities to
// each product's stock — faithful to the legacy Received_Inventory save.
export async function createReceiving(
  header: Partial<Receiving>,
  items: Partial<ReceivingDetailItem & { product_id: string | null }>[],
): Promise<Receiving> {
  const { data, error } = await supabase.from('receiving_headers').insert(header).select().single()
  if (error) throw error
  const headerId = (data as Receiving).id
  if (items.length) {
    const rows = items.map((it, i) => ({ ...it, receiving_header_id: headerId, order_number: it.order_number ?? i + 1 }))
    const { error: detErr } = await supabase.from('receiving_detail').insert(rows)
    if (detErr) throw detErr
    // Bump stock for every received line that maps to a known product.
    for (const it of items) {
      if (it.product_id && it.qty) {
        try { await adjustProductQuantity(it.product_id, Number(it.qty)) }
        catch (e) { console.error('stock adjust failed for', it.product_id, e) }
      }
    }
  }
  return data as Receiving
}

// ---- Inventory Pictures (plant reference library) ----------------------------

export function listInventoryPictures(opts: ListOptions = {}) {
  return queryList<InventoryPicture>('inventory_pictures', opts, {
    searchCols: ['name', 'plant_type', 'inventory_type'],
    defaultSort: { column: 'name', dir: 'asc' },
  })
}

// Picture-library picker for the product form. Legacy products carried a shared
// picture_id (= inventory_pictures.legacy_id) so many items reuse one photo. We
// only surface entries that actually have an image, newest/name-sorted.
export async function searchPicturesForProduct(term: string, limit = 40): Promise<InventoryPicture[]> {
  let q = supabase.from('inventory_pictures')
    .select('id, legacy_id, name, inventory_type, plant_type, image_url')
    .not('image_url', 'is', null)
  const t = term.trim()
  if (t) q = q.or(`name.ilike.%${t}%,plant_type.ilike.%${t}%,inventory_type.ilike.%${t}%`)
  const { data, error } = await q.order('name').limit(limit)
  if (error) throw error
  return (data ?? []) as unknown as InventoryPicture[]
}

// Resolve the picture a product points at via its legacy picture_id. A picture_id
// can exist in several folders (Trees/Shrubs/…); prefer one matching the product's
// inventory_type, then any row that has an image.
export async function getPictureByLegacyId(legacyId: number, inventoryType?: string | null): Promise<InventoryPicture | null> {
  const { data, error } = await supabase.from('inventory_pictures')
    .select('id, legacy_id, name, inventory_type, plant_type, image_url')
    .eq('legacy_id', legacyId)
  if (error) throw error
  const rows = (data ?? []) as unknown as InventoryPicture[]
  if (rows.length === 0) return null
  const withImg = rows.filter(r => r.image_url)
  const pool = withImg.length ? withImg : rows
  if (inventoryType) {
    const typed = pool.find(r => (r.inventory_type ?? '').toLowerCase() === inventoryType.toLowerCase())
    if (typed) return typed
  }
  return pool[0]
}

// The five fixed inventory-picture categories, sourced from the legacy IMS
// picture tables (TreePictures/ShrubPictures/PerennialPictures/HardgoodPictures/
// FruitPictures). inventory_type is therefore a closed set, not free text.
export const INVENTORY_PICTURE_TYPES = ['Trees', 'Shrubs', 'Perennials', 'Hardgoods', 'Fruit'] as const

// Distinct plant_type values already present in the library, for the plant-type
// dropdown (legacy [Plant Type] was a free-text sub-classification per row).
export async function listPicturePlantTypes(): Promise<string[]> {
  const { data, error } = await supabase.from('inventory_pictures')
    .select('plant_type').not('plant_type', 'is', null)
  if (error) throw error
  return [...new Set((data ?? []).map(r => (r.plant_type as string).trim()).filter(Boolean))].sort()
}

// ---- Photo storage (Supabase Storage bucket that replaced the Z: drive) ------
const PICTURES_BUCKET = 'inventory-pictures'

// Upload an image file and return its public URL. Files are namespaced per
// folder (e.g. 'products', 'library') with a random name to avoid collisions.
export async function uploadInventoryImage(file: File, folder = 'uploads'): Promise<string> {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg'
  const path = `${folder}/${crypto.randomUUID()}.${ext}`
  const { error } = await supabase.storage.from(PICTURES_BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type || undefined, cacheControl: '3600' })
  if (error) throw error
  return supabase.storage.from(PICTURES_BUCKET).getPublicUrl(path).data.publicUrl
}

// App-managed upload folders. Migrated originals live under the legacy plant-type
// folders (Trees/Shrubs/Perennials/Hardgoods/Fruits) and may be SHARED by several
// rows, so we never delete those — only files this app uploaded.
const MANAGED_FOLDERS = ['products/', 'library/', 'uploads/']

// Best-effort delete of an APP-UPLOADED image, given its public URL. No-ops for
// migrated originals (shared) or URLs outside our bucket. Never throws.
export async function deleteInventoryImageByUrl(url: string | null | undefined): Promise<void> {
  if (!url) return
  const marker = `/object/public/${PICTURES_BUCKET}/`
  const idx = url.indexOf(marker)
  if (idx === -1) return
  const path = decodeURIComponent(url.slice(idx + marker.length))
  if (!path || !MANAGED_FOLDERS.some(f => path.startsWith(f))) return
  try { await supabase.storage.from(PICTURES_BUCKET).remove([path]) } catch { /* ignore */ }
}

// ---- Customer site-visit media (photos/videos/documents) -------------------
// Private bucket: objects are fetched via short-lived signed URLs, not public.
const CUSTOMER_MEDIA_BUCKET = 'customer-media'

function mediaKindFor(file: File): CustomerMediaKind {
  const t = (file.type || '').toLowerCase()
  if (t.startsWith('image/')) return 'photo'
  if (t.startsWith('video/')) return 'video'
  return 'document'
}

// Upload a media file for a customer to the private bucket. Returns the stored
// row (with a freshly signed file_url for immediate preview).
export async function addCustomerMedia(args: {
  file: File
  customerId: string
  appointmentId?: string | null
  title?: string | null
  notes?: string | null
  createdBy?: string | null
}): Promise<CustomerMedia> {
  const { file, customerId, appointmentId = null, title = null, notes = null, createdBy = null } = args
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin'
  const path = `${customerId}/${crypto.randomUUID()}.${ext}`
  const { error: upErr } = await supabase.storage.from(CUSTOMER_MEDIA_BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type || undefined, cacheControl: '3600' })
  if (upErr) throw upErr

  const row: Partial<CustomerMedia> = {
    customer_id: customerId,
    appointment_id: appointmentId,
    kind: mediaKindFor(file),
    file_path: path,
    file_name: file.name,
    mime_type: file.type || null,
    size_bytes: file.size,
    title: title || file.name,
    notes,
    created_by: createdBy,
  }
  const { data, error } = await supabase.from('customer_media').insert(row).select().single()
  if (error) {
    // Roll back the orphaned object if the row insert failed.
    try { await supabase.storage.from(CUSTOMER_MEDIA_BUCKET).remove([path]) } catch { /* ignore */ }
    throw error
  }
  const out = data as CustomerMedia
  out.file_url = await signedMediaUrl(out.file_path)
  return out
}

// Short-lived signed URL for a private-bucket object (default 1 hour).
export async function signedMediaUrl(path: string, expiresInSeconds = 3600): Promise<string | null> {
  const { data, error } = await supabase.storage.from(CUSTOMER_MEDIA_BUCKET).createSignedUrl(path, expiresInSeconds)
  if (error) return null
  return data?.signedUrl ?? null
}

// List a customer's media (newest first), each with a signed preview URL.
export async function listCustomerMedia(customerId: string): Promise<CustomerMedia[]> {
  const { data, error } = await supabase.from('customer_media')
    .select('*').eq('customer_id', customerId).order('created_at', { ascending: false })
  if (error) throw error
  const rows = (data ?? []) as CustomerMedia[]
  await Promise.all(rows.map(async r => { r.file_url = await signedMediaUrl(r.file_path) }))
  return rows
}

export async function updateCustomerMedia(id: string, patch: Partial<CustomerMedia>): Promise<void> {
  const { error } = await supabase.from('customer_media')
    .update({ title: patch.title, notes: patch.notes }).eq('id', id)
  if (error) throw error
}

export async function deleteCustomerMedia(row: CustomerMedia): Promise<void> {
  const { error } = await supabase.from('customer_media').delete().eq('id', row.id)
  if (error) throw error
  try { await supabase.storage.from(CUSTOMER_MEDIA_BUCKET).remove([row.file_path]) } catch { /* ignore */ }
}

export async function createInventoryPicture(p: Partial<InventoryPicture>): Promise<InventoryPicture> {
  const { data, error } = await supabase.from('inventory_pictures').insert(p).select().single()
  if (error) throw error
  return data as InventoryPicture
}

export async function updateInventoryPicture(id: string, p: Partial<InventoryPicture>): Promise<InventoryPicture> {
  const { data, error } = await supabase.from('inventory_pictures').update(p).eq('id', id).select().single()
  if (error) throw error
  return data as InventoryPicture
}

export async function deleteInventoryPicture(row: InventoryPicture): Promise<void> {
  const { error } = await supabase.from('inventory_pictures').delete().eq('id', row.id)
  if (error) throw error
  await deleteInventoryImageByUrl(row.image_url)
}

// Faithful port of EstimatePictures_Report.ReportStart/Detail_Format:
//   for each line item, find its inventory item by Inventory_Code where
//   Picture_ID > 0 AND PrintPicture = True, then show the picture/description
//   for that Picture_ID.
// Migrated mapping: estimate/bill item.legacy_inventory_code ->
//   products.legacy_ims_inventory_code -> products.picture_id -> the
//   inventory_pictures row whose legacy_id = picture_id (image + care text).
// The legacy PlantType (which selected the Perennials/Shrubs/Trees/... folder)
// was not preserved on products, so when one picture_id exists across multiple
// folders we disambiguate by best name match to the item.
function nameTokens(s: string | null | undefined): Set<string> {
  return new Set(
    (s ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length > 2),
  )
}

function bestPictureByName(candidates: InventoryPicture[], target: string | null | undefined): InventoryPicture {
  if (candidates.length === 1) return candidates[0]
  const want = nameTokens(target)
  let best = candidates[0]
  let bestScore = -1
  for (const c of candidates) {
    const have = nameTokens(c.name)
    let score = 0
    for (const t of want) if (have.has(t)) score++
    if (score > bestScore) { bestScore = score; best = c }
  }
  return best
}

export async function getReportPictures(
  items: Array<{ legacy_inventory_code: number | null; item_name: string | null }>,
): Promise<InventoryPicture[]> {
  const codes = [...new Set(
    items.map(i => i.legacy_inventory_code).filter((c): c is number => typeof c === 'number'),
  )]
  if (codes.length === 0) return []

  // 1) Line item -> inventory item (faithful filter: Picture_ID > 0 AND PrintPicture).
  const { data: prods, error: pErr } = await supabase
    .from('products')
    .select('legacy_ims_inventory_code, picture_id, name, print_picture')
    .in('legacy_ims_inventory_code', codes)
    .gt('picture_id', 0)
    .eq('print_picture', true)
  if (pErr) throw pErr
  if (!prods || prods.length === 0) return []

  const linkByCode = new Map<number, { picture_id: number; name: string | null }>()
  for (const p of prods as Array<{ legacy_ims_inventory_code: number | null; picture_id: number; name: string | null }>) {
    if (p.legacy_ims_inventory_code != null && !linkByCode.has(p.legacy_ims_inventory_code)) {
      linkByCode.set(p.legacy_ims_inventory_code, { picture_id: p.picture_id, name: p.name })
    }
  }
  if (linkByCode.size === 0) return []

  // 2) Picture_ID -> inventory_pictures (may have multiple folders per id).
  const picIds = [...new Set([...linkByCode.values()].map(v => v.picture_id))]
  const { data: pics, error: iErr } = await supabase
    .from('inventory_pictures')
    .select('*')
    .in('legacy_id', picIds)
    .not('image_url', 'is', null)
  if (iErr) throw iErr

  const picsById = new Map<number, InventoryPicture[]>()
  for (const ip of (pics ?? []) as InventoryPicture[]) {
    if (ip.legacy_id == null) continue
    const arr = picsById.get(ip.legacy_id) ?? []
    arr.push(ip)
    picsById.set(ip.legacy_id, arr)
  }

  // 3) Resolve in item order, disambiguating by name, de-duplicating pictures.
  const out: InventoryPicture[] = []
  const seen = new Set<string>()
  for (const it of items) {
    if (it.legacy_inventory_code == null) continue
    const link = linkByCode.get(it.legacy_inventory_code)
    if (!link) continue
    const candidates = picsById.get(link.picture_id)
    if (!candidates || candidates.length === 0) continue
    const pick = bestPictureByName(candidates, it.item_name ?? link.name)
    if (seen.has(pick.id)) continue
    seen.add(pick.id)
    out.push(pick)
  }
  return out
}

// ---- Lookups -----------------------------------------------------------------

export async function getTaxRates(): Promise<TaxRate[]> {
  const { data, error } = await supabase.from('tax_rates').select('*')
  if (error) throw error
  return (data ?? []) as TaxRate[]
}

export async function getDefaultTaxRate(): Promise<number> {
  const rates = await getTaxRates()
  const def = rates.find(r => r.is_default) ?? rates[0]
  return def?.rate ?? 0
}

export async function createTaxRate(t: Partial<TaxRate>): Promise<TaxRate> {
  const { data, error } = await supabase.from('tax_rates').insert(t).select().single()
  if (error) throw error
  return data as TaxRate
}

export async function updateTaxRate(id: string, t: Partial<TaxRate>): Promise<TaxRate> {
  const { data, error } = await supabase.from('tax_rates').update(t).eq('id', id).select().single()
  if (error) throw error
  return data as TaxRate
}

export async function getDepartments(): Promise<Department[]> {
  const { data, error } = await supabase.from('departments').select('*').order('name')
  if (error) throw error
  return (data ?? []) as Department[]
}

// Generic lookup-table helpers used by the admin Departments/Categories pages.
export interface LookupRow { id: string; name: string; is_active?: boolean | null; [key: string]: unknown }

export async function listLookup(table: string, parentCol?: string, parentId?: string): Promise<LookupRow[]> {
  let q = supabase.from(table).select('*').order('name')
  if (parentCol && parentId) q = q.eq(parentCol, parentId)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as LookupRow[]
}

export async function createLookup(table: string, row: Record<string, unknown>): Promise<LookupRow> {
  const { data, error } = await supabase.from(table).insert(row).select().single()
  if (error) throw error
  return data as LookupRow
}

// ---- QuickBooks Export -------------------------------------------------------

export async function listBillsForExport(startDate: string, endDate: string): Promise<Bill[]> {
  const { data, error } = await supabase
    .from('bills')
    .select(`
      *,
      tax_rates!inner(id, state, county)
    `)
    .eq('qb_export', false)
    .eq('is_null_invoice', false)
    .gte('bill_date', startDate)
    .lt('bill_date', endDate)
    .order('bill_date', { ascending: true })
  if (error) throw error
  return (data ?? []) as Bill[]
}

export async function markBillsExported(billIds: string[]): Promise<void> {
  const { error } = await supabase.from('bills').update({ qb_export: true }).in('id', billIds)
  if (error) throw error
}

export async function listQBAccounts(): Promise<QB_Account[]> {
  const { data, error } = await supabase.from('qb_accounts').select('*')
  if (error) throw error
  return (data ?? []) as QB_Account[]
}

export async function updateLookup(table: string, id: string, row: Record<string, unknown>): Promise<LookupRow> {
  const { data, error } = await supabase.from(table).update(row).eq('id', id).select().single()
  if (error) throw error
  return data as LookupRow
}

// ---- Counts for dashboards ---------------------------------------------------

export async function getTableCount(table: string): Promise<number> {
  const { count, error } = await supabase.from(table).select('*', { count: 'exact', head: true })
  if (error) throw error
  return count ?? 0
}

// ---- Site Plans -------------------------------------------------------------

export function listSitePlans(opts: ListOptions = {}) {
  return queryList<SitePlan & { customer_name?: string }>('site_plans', opts, {
    searchCols: ['name', 'address'],
    defaultSort: { column: 'updated_at', dir: 'desc' },
  })
}

export async function getSitePlan(id: string): Promise<{ plan: SitePlan; items: SitePlanItem[] } | null> {
  const { data: plan, error } = await supabase.from('site_plans').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  if (!plan) return null
  const { data: items } = await supabase
    .from('site_plan_items')
    .select('*')
    .eq('site_plan_id', id)
    .order('sort_order')
  return { plan: plan as SitePlan, items: (items ?? []) as SitePlanItem[] }
}

export async function saveSitePlan(
  id: string | null,
  planData: Partial<SitePlan>,
  items: SitePlanItem[]
): Promise<SitePlan> {
  let savedPlan: SitePlan
  if (id) {
    const { data, error } = await supabase.from('site_plans').update(planData).eq('id', id).select().single()
    if (error) throw error
    savedPlan = data as SitePlan
  } else {
    const { data, error } = await supabase.from('site_plans').insert(planData).select().single()
    if (error) throw error
    savedPlan = data as SitePlan
  }
  // Upsert all items
  if (items.length > 0) {
    const rows = items.map((it, i) => ({ ...it, site_plan_id: savedPlan.id, sort_order: i }))
    const { error } = await supabase.from('site_plan_items').upsert(rows, { onConflict: 'id' })
    if (error) throw error
    // Delete removed items
    const ids = items.map(it => it.id)
    await supabase.from('site_plan_items').delete().eq('site_plan_id', savedPlan.id).not('id', 'in', `(${ids.map(i => `"${i}"`).join(',')})`)
  } else {
    await supabase.from('site_plan_items').delete().eq('site_plan_id', savedPlan.id)
  }
  return savedPlan
}

// Product palette for the Site Plan designer. Searches server-side across the
// real product columns (name + plant taxonomy + sku) so any item is findable,
// not just the first page of alphabetical results.
export async function listProductsForPalette(search = ''): Promise<Product[]> {
  let q = supabase
    .from('products')
    .select('id, name, genus, species, variety, sku, retail_price, qty_remaining, qty_on_hand, inventory_type, picture_id, image_url')
    .eq('is_active', true)
  const t = search.trim()
  if (t) {
    const ors = [`name.ilike.%${t}%`, `genus.ilike.%${t}%`, `species.ilike.%${t}%`, `variety.ilike.%${t}%`]
    if (/^\d+$/.test(t)) ors.push(`sku.eq.${t}`)
    q = q.or(ors.join(','))
  }
  const { data, error } = await q.order('name').limit(t ? 100 : 200)
  if (error) throw error
  return (data ?? []) as unknown as Product[]
}
